import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/modules/user/auth/stores/auth'
import { useConfigStore } from '@/stores/config'
import ApiService from '@/core/services/ApiService'
import * as Sentry from '@sentry/vue'
import purchaseOrdersRoutes from '@/modules/purchasing/purchase-orders/routes/purchaseOrdersRouter'
import suppliersRoutes from '@/modules/purchasing/suppliers/routes/suppliersRouter'
import invoicesRoutes from '@/modules/purchasing/invoices/routes/invoicesRouter'
import documentsRouter from '@/modules/ism-documents/routes/ismDocumentsRouter'
import vesselsRoutes from '@/modules/vessel/routes/vessels'
import drillsRoutes from '@/modules/drills/routes/drillsRouter'
import surveysRoutes from '@/modules/operations/routes/surveysRouter'
import tradingCertificatesRoutes from '@/modules/certificates/trading-certificates/routes/tradingCertificatesRoutes'
import equipmentCertificatesRoutes from '@/modules/certificates/equipment-certificates/routes/equipmentCertificatesRoutes'
import qrfsRoutes from '@/modules/qrf/routes/qrfsRouter'
import investigationReportsRoutes from '@/modules/qrf/routes/investigationReportsRoutes'
import vesselReportsRoutes from '@/modules/vessel-reports/routes/vesselReportsRouter'
import surveyScheduleRoutes from '@/modules/operations/routes/surveyScheduleRouter'
import audits from '@/modules/operations/routes/audits'
import externalServicesRoutes from '@/modules/services/routes/externalServicesRoutes'
import waterSamplesRoutes from '@/modules/technical/water-samples/routes/waterSamplesRouter'
import lubOilSamplesRoutes from '@/modules/technical/lub-oil-samples/routes/lubOilSamplesRoutes'
import pmsRoutes from '@/modules/pms/routes/pmsRoutes'
import inventoryRoutes from '@/modules/pms/routes/inventoryRoutes.ts'
import keyContactsRoutes from '@/modules/key-contacts/routes/keyContactsRoutes'
import usersRoutes from '@/modules/user/user/routes/usersRoutes'
import rolesRoutes from '@/modules/user/role/routes/rolesRoutes'
import officeCertificatesRoutes from '@/modules/office/office-certificates/routes/officeCertificatesRoutes'
import officeSurveysRoutes from '@/modules/operations/routes/officeSurveysRoutes'
import meetingsRoutes from '@/modules/meetings/routes/meetingsRouter'
import crewMembersRoutes from '@/modules/crewing/routes/crewMembersRoutes'
import voyagesRoutes from '@/modules/crewing/routes/voyagesRoutes'
import crewListsRoutes from '@/modules/crewing/routes/crewListsRoutes'
import portsRoutes from '@/modules/crewing/routes/portsRoutes'
import ranksRoutes from '@/modules/crewing/routes/ranksRoutes'
import medicalReportsRoutes from '@/modules/qrf/routes/medicalReportsRouter'
import payersRoutes from '@/modules/purchasing/payers/routes/payersRouter'
import { watch } from 'vue'
import requisitionsRoutes from '@/modules/purchasing/requisitions/routes/requisitionsRoutes.ts'
import rfqsRoutes from '@/modules/purchasing/rfqs/routes/rfqsRouter'
import featureFlagsRoutes from '@/modules/user/admin/routes/featureFlagsRoutes'
import organizationsRoutes from '@/modules/user/organization/routes/organizationsRoutes'
import sessionReportsRoutes from '@/modules/user/session-reports/routes/sessionReportsRoutes'
import actionsRoutes from '@/modules/actions/routes/actionsRouter'
import dockingRoutes from '@/modules/docking/routes/dockingRouter'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/dashboard',
    component: () => import('@/layouts/default-layout/DefaultLayout.vue'),
    meta: {
      middleware: 'auth'
    },
    children: [
      {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('@/modules/dashboard/views/DashboardView.vue'),
        meta: {
          pageTitle: 'Dashboard',
          breadcrumbs: ['Dashboard']
        }
      },
      {
        path: '/users/profile',
        name: 'users.profiles',
        component: () => import('@/modules/user/user/views/UsersProfile.vue'),
        meta: {
          pageTitle: 'User Profile',
          breadcrumbs: ['User Profile']
        }
      },
      ...purchaseOrdersRoutes,
      ...requisitionsRoutes,
      ...rfqsRoutes,
      ...suppliersRoutes,
      ...invoicesRoutes,
      ...documentsRouter,
      ...vesselsRoutes,
      ...drillsRoutes,
      ...surveysRoutes,
      ...tradingCertificatesRoutes,
      ...equipmentCertificatesRoutes,
      ...qrfsRoutes,
      ...investigationReportsRoutes,
      ...vesselReportsRoutes,
      ...surveyScheduleRoutes,
      ...audits,
      ...externalServicesRoutes,
      ...waterSamplesRoutes,
      ...lubOilSamplesRoutes,
      ...pmsRoutes,
      ...inventoryRoutes,
      ...keyContactsRoutes,
      ...usersRoutes,
      ...rolesRoutes,
      ...officeCertificatesRoutes,
      ...officeSurveysRoutes,
      ...meetingsRoutes,
      ...crewMembersRoutes,
      ...voyagesRoutes,
      ...crewListsRoutes,
      ...portsRoutes,
      ...ranksRoutes,
      ...medicalReportsRoutes,
      ...payersRoutes,
      ...featureFlagsRoutes,
      ...organizationsRoutes,
      ...sessionReportsRoutes,
      ...actionsRoutes,
      ...dockingRoutes
    ]
  },
  {
    path: '/',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: '/sign-in',
        name: 'sign-in',
        component: () => import('@/modules/user/auth/views/SignIn.vue'),
        meta: {
          pageTitle: 'Sign In',
          guest: true
        }
      },
      {
        path: '/password-reset/:token',
        name: 'password-reset',
        component: () => import('@/modules/user/auth/views/PasswordReset.vue'),
        meta: {
          pageTitle: 'Password reset'
        }
      },
      {
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('@/modules/user/auth/views/ResetPassword.vue'),
        meta: {
          pageTitle: 'Reset password'
        }
      },
      {
        path: '/reset-password/requested',
        name: 'reset-password-requested',
        component: () => import('@/modules/user/auth/views/PasswordResetRequested.vue'),
        meta: {
          pageTitle: 'Reset link sent'
        }
      }
    ]
  },
  {
    path: '/',
    component: () => import('@/layouts/SystemLayout.vue'),
    children: [
      {
        // the 404 route, when none of the above matches
        path: '/404',
        name: '404',
        component: () => import('@/views/Error404View.vue'),
        meta: {
          pageTitle: 'Error 404'
        }
      },
      {
        path: '/500',
        name: '500',
        component: () => import('@/views/Error500View.vue'),
        meta: {
          pageTitle: 'Error 500'
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    // If the route has a hash, scroll to the section with the specified ID; otherwise, scroll to the top of the page.
    if (to.hash) {
      return {
        el: to.hash,
        top: 80,
        behavior: 'smooth'
      }
    } else {
      return {
        top: 0,
        left: 0,
        behavior: 'smooth'
      }
    }
  }
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const configStore = useConfigStore()

  // current page view title
  document.title = `${to.meta.pageTitle} - ${import.meta.env.VITE_APP_NAME}`

  // reset config to initial state
  configStore.resetLayoutConfig()

  // redirect already-authenticated users away from guest-only pages (e.g. /sign-in)
  if (to.meta.guest) {
    if (authStore.isAuthenticated && !authStore.isAuthExpired) {
      next({ name: 'dashboard' })
      return
    }
    next()
    return
  }

  // before page access check if page requires authentication
  if (to.meta.middleware == 'auth') {
    // If we have a valid auth state and it's not expired, proceed
    if (authStore.isAuthenticated && !authStore.isAuthExpired) {
      next()
      return
    }

    // If we're already checking auth, wait for it to complete
    if (authStore.isCheckingAuth) {
      await new Promise((resolve) => {
        const unwatch = watch(
          () => authStore.isCheckingAuth,
          (newValue) => {
            if (!newValue) {
              unwatch()
              resolve(true)
            }
          }
        )
      })
      if (authStore.isAuthenticated) {
        next()
        return
      }
    }

    // Start auth check
    authStore.setCheckingAuth(true)
    try {
      const response = await ApiService.get('/api/users/me')
      if (response.status === 200) {
        authStore.setAuth(response.data.data)
        next()
      } else {
        authStore.purgeAuth()
        next({ name: 'sign-in' })
      }
    } catch (error) {
      console.error(error)
      authStore.purgeAuth()
      next({ name: 'sign-in' })
    } finally {
      authStore.setCheckingAuth(false)
    }
  } else {
    next()
  }
})

router.afterEach(() => {
  // Ensure the page is scrolled to the top
  window.scrollTo(0, 0)
})

router.onError((error, to) => {
  // Log the error to Sentry
  Sentry.captureException(error, {
    extra: {
      route: to?.fullPath,
      errorMessage: error.message
    }
  })

  // Handle dynamic import failures
  if (
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Importing a module script failed')
  ) {
    console.warn('Dynamic import failed, attempting to recover...', {
      error,
      route: to?.fullPath
    })

    // If we have a route, try to navigate to it directly
    if (to?.fullPath) {
      window.location.href = to.fullPath
    } else {
      // If no route, reload the current page
      window.location.reload()
    }
  }
})

export default router
